/**
 * Normalisation + formatting for Firestore Timestamps and friends.
 *
 * Firestore values reach the frontend in three shapes depending on path:
 *   - Live SDK Timestamp object with .toDate()        (direct Firestore reads)
 *   - Serialized object { _seconds, _nanoseconds }    (callable Cloud Function returns)
 *   - ISO string or epoch number                      (legacy/manual paths)
 *
 * `toDate` collapses all three into a JS Date (or null when unparseable).
 * The format helpers wrap that with en-AU locale defaults used across the app.
 */

export function toDate(ts: unknown): Date | null {
  if (ts == null) return null;

  if (typeof (ts as { toDate?: () => Date }).toDate === 'function') {
    const d = (ts as { toDate: () => Date }).toDate();
    return isNaN(d.getTime()) ? null : d;
  }

  if (typeof ts === 'object') {
    const raw = (ts as { _seconds?: unknown; seconds?: unknown })._seconds
      ?? (ts as { _seconds?: unknown; seconds?: unknown }).seconds;
    // Firestore int64 fields can deserialize as either number or string depending on
    // how the value was stored. Accept both.
    const seconds = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN;
    if (Number.isFinite(seconds)) {
      const d = new Date(seconds * 1000);
      return isNaN(d.getTime()) ? null : d;
    }
  }

  if (typeof ts === 'string' || typeof ts === 'number') {
    const d = new Date(ts);
    return isNaN(d.getTime()) ? null : d;
  }

  return null;
}

/** "5 Apr 2026" */
export function formatShortDate(ts: unknown, fallback = '-'): string {
  const d = toDate(ts);
  if (!d) return fallback;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** "5 Apr 2026, 2:30 pm" */
export function formatDateTime(ts: unknown, fallback = '-'): string {
  const d = toDate(ts);
  if (!d) return fallback;
  const date = d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  const time = d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true });
  return `${date}, ${time}`;
}

/** Whole days between two timestamps; returns 0 if either is unparseable. */
export function daysBetween(a: unknown, b: unknown): number {
  const da = toDate(a);
  const db = toDate(b);
  if (!da || !db) return 0;
  const ms = Math.abs(db.getTime() - da.getTime());
  return Math.max(0, Math.floor(ms / 86400000));
}
